import { useRef, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Captcha, CAPTCHA_MSG, RecaptchaNotice } from "./Captcha";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = "w-full border border-[#E62314] bg-white/60 px-4 py-3 text-base text-black placeholder:text-[#8a8580] outline-none focus:ring-4 focus:ring-[#E62314]/15";

export const UnsubscribeDialog = ({ open, onOpenChange }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [loading, setLoading] = useState(false);
  const captcha = useRef(null);

  const change = (o) => { onOpenChange(o); if (!o) { setDone(""); setError(""); setName(""); setEmail(""); setToken(""); } };
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setError("Please enter your email address.");
    if (!token) return setError(CAPTCHA_MSG);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/unsubscribe-request`, { email: email.trim(), name: name.trim(), recaptcha_token: token });
      setDone(data?.email ?? email.trim());
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Please enter a valid email address.");
    } finally { captcha.current?.reset(); setToken(""); setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent data-testid="unsubscribe-dialog" className="max-h-[90vh] overflow-y-auto rounded-none bg-[#FBF9F5] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Unsubscribe</DialogTitle>
          <DialogDescription>Remove your email from the Cresway Media mailing list.</DialogDescription>
        </DialogHeader>
        {done ? (
          <p data-testid="unsubscribe-success" className="bg-[#ECEAE6] p-5 text-base">
            <strong className="break-words">{done}</strong> has been removed from our mailing list. You won't receive any more updates from us.
          </p>
        ) : (
          <form onSubmit={submit} noValidate className="flex flex-col" data-testid="unsubscribe-form">
            <label htmlFor="unsub-name" className="mb-2 text-sm font-bold">Full Name <span className="font-normal text-[#57534E]">(optional)</span></label>
            <input id="unsub-name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Your name" autoComplete="name" className={`${input} mb-5`} data-testid="unsubscribe-name-input" />
            <label htmlFor="unsub-email" className="mb-2 text-sm font-bold">Email Address <span className="text-[#E62314]">*</span></label>
            <input id="unsub-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" autoComplete="email" className={input} data-testid="unsubscribe-email-input" />
            <Captcha ref={captcha} onChange={setToken} testId="unsubscribe-recaptcha" />
            <button type="submit" disabled={loading} data-testid="unsubscribe-submit"
              className="mt-6 flex items-center justify-center gap-2 bg-[#0A0A0A] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#E62314] disabled:opacity-40">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}Unsubscribe
            </button>
            {error && <p data-testid="unsubscribe-error" className="mt-4 text-sm font-semibold text-[#C21B0E]">{error}</p>}
            <RecaptchaNotice testId="unsubscribe-recaptcha-notice" className="mt-4" />
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
